import {
  useCallback,
  useRef,
} from 'react';
import type {
  ReactElement,
  ReactNode,
} from 'react';

import styled, {
  useTheme,
} from 'styled-components';

import { DropdownOption } from './DropdownOption';

import type {
  HeaderDropdownOption,
} from './types';
import { Dropdown, DropdownContentRenderProps } from '../Dropdown';

const StyledWrapper = styled.div(({
  theme: {
    header,
    layout,
  },
}) => ({
  display: 'flex',
  float: 'right',
  position: 'relative',
  marginLeft: header.DROPDOWN_MARGIN_LEFT,
  height: layout.HEADER_HEIGHT,
}));

export const StyledButton = styled.button(({
  disabled,

  theme: {
    colors,
    fonts,
  },
}) => ({
  ...fonts.DEFAULT,
  display: 'flex',
  alignItems: 'center',
  padding: 0,
  border: 'none',
  backgroundColor: 'transparent',
  color: colors.NEUTRAL_DEFAULT,
  cursor: disabled ? 'default' : 'pointer',
  outline: 'none',

  ':hover': disabled ? undefined : {
    color: colors.ACTION_HOVER,
  },

  ':active': disabled ? undefined : {
    color: colors.ACTION_PRESSED,
  },
}));

export const StyledAvatar = styled.div<{
  image: string;
}>(({
  image,

  theme: {
    indents,
  },
}) => ({
  width: 24,
  height: 24,
  borderRadius: 12,
  marginRight: indents.INDENT_1,
  backgroundSize: 'cover',
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'center',
  backgroundImage: `url("${image}")`,
}));

export const StyledAvatarPlaceholder = styled.div(({
  theme: {
    indents,
  },
}) => ({
  display: 'flex',
  width: 24,
  height: 24,
  marginRight: indents.INDENT_1,
}));

export const StyledName = styled.div({
  overflow: 'hidden',
  whiteSpace: 'nowrap',
});

export const StyledCaret = styled.div<{ $isOpen: boolean }>(({
  $isOpen,
  theme: {
    colors,
    header,
  },
}) => ({
  display: 'flex',
  color: colors[header.DROPDOWN_CARET_COLOR],
  paddingLeft: 4,

  '& svg': {
    transform: `rotate(${$isOpen ? '180deg' : '0deg'})`,
  },
}));

export type HeaderDropdownProps = {
  showAvatar: boolean;
  avatar?: string;
  options?: HeaderDropdownOption[];
  userName?: ReactNode;
};

export function HeaderDropdown({
  showAvatar,
  avatar = undefined,
  userName = undefined,
  options = undefined,
}: HeaderDropdownProps): ReactElement {
  const {
    colors,
    header,
  } = useTheme();

  const rootRef = useRef<HTMLDivElement>(null);

  const hasOptions = options
    ? options.length > 0
    : false;

  const renderContent = useCallback(({ close }: DropdownContentRenderProps) => (
    options?.map((option, index) => (
      <DropdownOption
        option={option}
        closeDropdown={close}
        key={index}
      />
    ))), [options]);

  return (
    <StyledWrapper
      ref={rootRef}
    >
      <Dropdown renderContent={renderContent}>
        {({
          isDropdownOpen,
          onClick,
          ref,
        }) => (
          <StyledButton
            type="button"
            disabled={!hasOptions}
            onClick={onClick}
            ref={ref}
          >
            {
              showAvatar && (
                avatar ? (
                  <StyledAvatar
                    image={avatar}
                  />
                ) : (
                  <StyledAvatarPlaceholder>
                    <svg
                      width={24}
                      height={24}
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <circle
                        cx="12"
                        cy="12"
                        r="12"
                        fill={colors.NEUTRAL_INPUT}
                      />

                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M11.9997 5.33325C10.158 5.33325 8.66634 6.82492 8.66634 8.66659C8.66634 10.5083 10.158 11.9999 11.9997 11.9999C13.8413 11.9999 15.333 10.5083 15.333 8.66659C15.333 6.82492 13.8413 5.33325 11.9997 5.33325ZM13.7497 8.66659C13.7497 7.69992 12.9663 6.91659 11.9997 6.91659C11.033 6.91659 10.2497 7.69992 10.2497 8.66659C10.2497 9.63325 11.033 10.4166 11.9997 10.4166C12.9663 10.4166 13.7497 9.63325 13.7497 8.66659ZM17.083 16.1666C17.083 15.6333 14.4747 14.4166 11.9997 14.4166C9.52467 14.4166 6.91634 15.6333 6.91634 16.1666V17.0833H17.083V16.1666ZM5.33301 16.1666C5.33301 13.9499 9.77467 12.8333 11.9997 12.8333C14.2247 12.8333 18.6663 13.9499 18.6663 16.1666V18.6666H5.33301V16.1666Z"
                        fill={colors.NEUTRAL_WHITE}
                      />
                    </svg>
                  </StyledAvatarPlaceholder>
                )
              )
            }

            <StyledName>
              {userName}
            </StyledName>

            {options && options.length > 0 && (
            <StyledCaret $isOpen={isDropdownOpen}>
              {header.DROPDOWN_CARET_ICON}
            </StyledCaret>
            )}
          </StyledButton>
        )}
      </Dropdown>
    </StyledWrapper>
  );
}
